import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Crown, CheckCircle2, Loader2 } from "lucide-react";
import { http, errMsg, phone10 } from "@/lib/data";

const VERTICALS = [["mutual_funds", "Mutual Funds & ETFs"], ["stocks", "Stocks"], ["insurance", "Insurance"], ["real_estate", "Real Estate"],
  ["gold_commodities", "Gold & Commodities"], ["forex", "Forex"], ["crypto", "Crypto"], ["fixed_income", "FDs & Bonds"]];
const EMPTY = { name: "", email: "", phone: "", city: "", state: "", verticals: [] };
const inp = "w-full h-11 rounded-lg border border-slate-300 px-3 outline-none focus:ring-2 focus:ring-[#10B981]";

const ClubForm = ({ onDone }) => {
  const [v, setV] = useState(EMPTY);
  const [st, setSt] = useState({ loading: false, error: "" });
  const set = (k) => (e) => setV({ ...v, [k]: k === "phone" ? phone10(e.target.value) : e.target.value });
  const toggle = (id) => setV({ ...v, verticals: v.verticals.includes(id) ? v.verticals.filter((x) => x !== id) : [...v.verticals, id] });
  const submit = async (e) => {
    e.preventDefault();
    if (!v.verticals.length) return setSt({ loading: false, error: "Pick at least one investment interest" });
    setSt({ loading: true, error: "" });
    try { await http.post("/club/join", v); onDone(v.name); } catch (err) { setSt({ loading: false, error: errMsg(err) }); }
  };
  return (
    <form onSubmit={submit} className="space-y-3 mt-2" data-testid="irie-club-form">
      <input data-testid="club-name" required minLength={2} placeholder="Full name" value={v.name} onChange={set("name")} className={inp} />
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="club-email" required type="email" placeholder="Email" value={v.email} onChange={set("email")} className={inp} />
        <input data-testid="club-phone" required inputMode="numeric" maxLength={16} placeholder="10-digit mobile" value={v.phone} onChange={set("phone")} className={inp} />
        <input data-testid="club-city" required minLength={2} placeholder="City" value={v.city} onChange={set("city")} className={inp} />
        <input data-testid="club-state" placeholder="State (optional)" value={v.state} onChange={set("state")} className={inp} />
      </div>
      <p className="text-sm font-semibold text-slate-700 pt-1">Interested in</p>
      <div className="flex flex-wrap gap-2">
        {VERTICALS.map(([id, l]) => (
          <button type="button" key={id} data-testid={`club-vertical-${id}`} onClick={() => toggle(id)}
            className={`h-9 px-3 rounded-full text-xs font-bold border transition-colors ${v.verticals.includes(id) ? "bg-[#1E293B] text-white border-[#1E293B]" : "border-slate-300 text-slate-600 hover:border-[#10B981]"}`}>{l}</button>
        ))}
      </div>
      {st.error && <p data-testid="club-error" className="text-sm text-red-600">{st.error}</p>}
      <button data-testid="club-submit" disabled={st.loading} className="w-full h-11 rounded-lg bg-[#10B981] hover:bg-[#059669] text-[#0F172A] font-bold inline-flex items-center justify-center gap-2">{st.loading && <Loader2 className="w-4 h-4 animate-spin" />}Join IRIE Club</button>
      <p className="text-[11px] text-slate-400">We'll only use these details to send DhanTrove newsletters on the topics you picked.</p>
    </form>
  );
};

export const IrieClubDialog = ({ open, onClose }) => {
  const [done, setDone] = useState("");
  const close = () => { setDone(""); onClose(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="irie-club-dialog" className="bg-white rounded-2xl max-w-lg w-[94vw] max-h-[92vh] overflow-y-auto">
        {done ? (
          <div data-testid="club-success" className="text-center py-6"><CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto" /><p className="font-display text-xl font-bold mt-3">Welcome to IRIE Club, {done.split(" ")[0]}!</p><p className="text-sm text-slate-600 mt-1">You'll start receiving newsletters on your chosen investment topics.</p><button data-testid="club-done" onClick={close} className="mt-5 h-10 px-5 rounded-lg bg-[#1E293B] text-white font-semibold">Done</button></div>
        ) : (
          <>
            <DialogHeader><DialogTitle className="font-display text-xl flex items-center gap-2"><Crown className="w-5 h-5 text-amber-500" />Join the IRIE Club</DialogTitle><DialogDescription>Free newsletters for Indian retail investors — market updates and new opportunities in the areas you care about.</DialogDescription></DialogHeader>
            <ClubForm onDone={setDone} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
